import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { setRedirect } from "@/lib/authRedirect";
import { ThemeProvider } from "@/context/ThemeContext";
import { ChitProvider } from "@/context/ChitContext";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import GroupDetail from "@/pages/GroupDetail";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AuthCallback from "@/pages/AuthCallback";
import Join from "@/pages/Join";
import Premium from "@/pages/Premium";
import Admin from "@/pages/Admin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

// Ecran protejat: dacă nu ești logat, mergi la /login.
function ProtectedLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-emerald-600 animate-spin" />
      </div>
    );
  }
  if (!user) {
    // Reținem destinația (ex: link de invitație) ca să revenim după login.
    setRedirect(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }
  return (
    <ChitProvider>
      <Outlet />
    </ChitProvider>
  );
}

function AppRouter() {
  const location = useLocation();
  // Detectăm întoarcerea de la Google (session_id în fragmentul URL) ÎNAINTE de rutare.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-parola" element={<ResetPassword />} />
      <Route path="/verifica-email" element={<VerifyEmail />} />
      {/* Publice: revenire din Stripe Checkout. Nu depind de ProtectedLayout
          ca să se afișeze imediat, indiferent de timing-ul de reautentificare
          după redirect-ul cross-domeniu de la checkout.stripe.com. */}
      <Route path="/plata/succes" element={<PaymentSuccess />} />
      <Route path="/plata/anulat" element={<PaymentCancel />} />
      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/grup/:groupId" element={<GroupDetail />} />
        <Route path="/invita/:groupId" element={<Join />} />
        <Route path="/premium" element={<Premium />} />
        <Route path="/admin" element={<Admin />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
          <BrowserRouter>
            {/* Container mobile-first: pe desktop simulăm ecranul unui telefon */}
            <div className="mx-auto max-w-md min-h-screen bg-background shadow-xl relative">
              <AppRouter />
            </div>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
